import { Box, Text, VStack, HStack, Icon, Container } from '@chakra-ui/react';
import { AlertTriangle, Phone, Mail, Users, FileText } from 'lucide-react';
import FaqItem from './FaqItem';
import { FagUrgency } from '@/constants/fagUrgency';
import { SectionHeader } from '@/components/SectionHeader';

const ContactInfo = ({
  type,
  contact,
  note,
}: {
  type: 'email' | 'phone';
  contact: string;
  note?: string;
}) => (
  <VStack gap={1} align="start" mb={2}>
    <HStack gap={2}>
      <Icon boxSize={4} color="fg.muted">
        {type === 'email' ? <Mail /> : <Phone />}
      </Icon>
      <Text fontWeight="medium" fontSize="sm">
        {contact}
      </Text>
    </HStack>
    {note && (
      <Text fontSize="xs" color="fg.muted" ml={6}>
        {note}
      </Text>
    )}
  </VStack>
);

const FaqView = () => {
  const faqData: {
    question: string;
    answer: string | React.ReactNode;
    icon: React.ReactNode;
    urgency: FagUrgency;
  }[] = [
    {
      question: 'Hur anmäler jag fel som inte är akuta?',
      answer: (
        <VStack gap={2} align="start">
          <Text fontSize="sm">För icke-akuta fel och brister, kontakta oss via e-post:</Text>
          <ContactInfo type="email" contact="info@palatium.se" />
          <Text fontSize="xs" color="fg.muted">
            Fel och brister i bostad eller andra delar av fastigheten ska alltid felanmälas.
          </Text>
        </VStack>
      ),
      icon: <Mail />,
      urgency: FagUrgency.Normal,
    },
    {
      question: 'Vad gör jag vid akuta händelser som översvämning?',
      answer: (
        <VStack gap={2} align="start">
          <Text fontSize="sm">Vid akuta händelser kontakta fastighetsjour:</Text>
          <ContactInfo type="phone" contact="010 470 57 70" note="Securitas fastighetsjour" />
          <Text fontSize="xs" color="orange.fg">
            Ring utan dröjsmål om felet kan orsaka skada på människor, fastigheten eller miljö.
          </Text>
        </VStack>
      ),
      icon: <AlertTriangle />,
      urgency: FagUrgency.Urgent,
    },
    {
      question: 'Jag har låst ut mig - vad gör jag?',
      answer: (
        <VStack gap={2} align="start">
          <Text fontSize="sm">Vid utelåsning kontakta:</Text>
          <ContactInfo type="phone" contact="010 470 57 70" note="Securitas fastighetsjour" />
          <Text fontSize="sm" color="fg.muted">
            Securitas debiterar en utryckningsavgift för låsjour.
          </Text>
        </VStack>
      ),
      icon: <Phone />,
      urgency: FagUrgency.Urgent,
    },
    {
      question: 'Nödsituation - vem ringer jag?',
      answer: (
        <VStack gap={2} align="start">
          <Text fontSize="sm">Vid akuta nödsituationer med fara för liv:</Text>
          <ContactInfo type="phone" contact="112" note="SOS Alarm" />
          <Text fontSize="sm" color="red.fg" fontWeight="medium">
            Ring 112 endast vid verkliga nödsituationer!
          </Text>
        </VStack>
      ),
      icon: <AlertTriangle />,
      urgency: FagUrgency.Emergency,
    },
    {
      question: 'Vilka krav finns för att hyra en bostad?',
      answer: (
        <VStack gap={3} align="start">
          <Text fontSize="sm">
            Palatium har ingen kö utan väljer den mest lämpliga hyresgästen. Grundkrav:
          </Text>
          <VStack gap={1} align="start" fontSize="sm" color="fg.muted">
            <Text>• Ansökan via HomeQ (skapa konto där)</Text>
            <Text>• Minst 18 år gammal</Text>
            <Text>• Fast inkomst från anställning, företag eller pension</Text>
            <Text>• Inkomst ska styrkas med intyg</Text>
            <Text>• Ca 1/3 av inkomsten ska täcka boendekostnader</Text>
            <Text>• Försörjningsstöd räknas inte som inkomst</Text>
            <Text>• Inga betalningsanmärkningar eller Kronofogdeskulder</Text>
            <Text>• Goda referenser från tidigare hyresvärd</Text>
          </VStack>
        </VStack>
      ),
      icon: <FileText />,
      urgency: FagUrgency.Normal,
    },
    {
      question: 'Hur många personer får bo i lägenheten?',
      answer: (
        <VStack gap={2} align="start">
          <Text fontSize="sm">För att förhindra trångboddhet finns följande rekommendationer:</Text>
          <VStack gap={1} align="start" fontSize="sm" color="fg.muted">
            <Text>
              <strong>1 rum och kök:</strong> max 2 personer
            </Text>
            <Text>
              <strong>2 rum och kök:</strong> max 3 personer
            </Text>
            <Text>
              <strong>3 rum och kök:</strong> max 5 personer
            </Text>
            <Text>
              <strong>4 rum och kök:</strong> max 6 personer
            </Text>
            <Text>
              <strong>5 rum och kök eller större:</strong> max 8 personer
            </Text>
          </VStack>
        </VStack>
      ),
      icon: <Users />,
      urgency: FagUrgency.Normal,
    },
  ];

  return (
    <Box bg="bg.subtle" minH="100vh">
      <Container maxW="3xl" py={{ base: '12', md: '20' }}>
        <VStack gap={{ base: '12', md: '16' }}>
          <SectionHeader
            eyebrow="För hyresgäster"
            heading="Vanliga frågor"
            description="Här hittar du svar på de vanligaste frågorna om boende hos Palatium."
          />

          <Box w="100%">
            {faqData.map((faq, index) => (
              <FaqItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                icon={faq.icon}
                urgency={faq.urgency}
              />
            ))}
          </Box>

          <Box bg="bg" p={4} borderRadius="md" w="100%" textAlign="center">
            <Text color="fg.muted" fontSize="sm">
              Hittar du inte svar på din fråga? Kontakta oss på{' '}
              <Text as="span" fontWeight="medium" color="fg">
                info@palatium.se
              </Text>
            </Text>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default FaqView;
